/**
 * theme.tsx — CIRCA console theme provider (NMW-246).
 *
 * Owns the light/dark/system choice, resolves it against the OS preference,
 * and reflects the resolved theme onto <html data-theme="..."> so the CSS
 * token layer (styles/global.css) can switch palettes. Sonner's toaster reads
 * the resolved theme through ThemedToaster so toasts match the active palette.
 *
 * Contract (load-bearing):
 *   - Choice   = 'light' | 'dark' | 'system'
 *   - Resolved = 'light' | 'dark'
 *   - localStorage key = 'circa_theme'; default when unset OR invalid = 'system'
 *   - resolve('system') => matchMedia('(prefers-color-scheme: dark)') ? 'dark' : 'light'
 *   - data-theme attribute updated on mount and on every resolved change
 *   - live OS-preference tracking while choice === 'system' (no reload)
 *
 * All window/document access is typeof-guarded (the app is CSR, but stay
 * defensive so SSR/prerender or a non-DOM test env cannot throw).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Toaster } from 'sonner';

export type Choice = 'light' | 'dark' | 'system';
export type Resolved = 'light' | 'dark';

const STORAGE_KEY = 'circa_theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';
const DEFAULT_CHOICE: Choice = 'system';

interface ThemeContextValue {
  choice: Choice;
  resolved: Resolved;
  setChoice: (c: Choice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isChoice(value: unknown): value is Choice {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredChoice(): Choice {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return DEFAULT_CHOICE;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isChoice(raw) ? raw : DEFAULT_CHOICE;
  } catch {
    return DEFAULT_CHOICE;
  }
}

function prefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia(MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

function resolve(choice: Choice): Resolved {
  if (choice === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return choice;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [choice, setChoiceState] = useState<Choice>(() => readStoredChoice());
  const [resolved, setResolved] = useState<Resolved>(() => resolve(readStoredChoice()));

  const setChoice = useCallback((next: Choice) => {
    setChoiceState(next);
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage unavailable (private mode / quota) — keep in-memory state */
      }
    }
  }, []);

  // Keep `resolved` in sync with the current choice.
  useEffect(() => {
    setResolved(resolve(choice));
  }, [choice]);

  // Reflect the resolved theme onto <html data-theme="...">.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  // Track OS preference live while following the system setting.
  useEffect(() => {
    if (choice !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mql = window.matchMedia(MEDIA_QUERY);
    const onChange = () => setResolved(mql.matches ? 'dark' : 'light');

    // Recompute immediately in case the preference changed between renders.
    onChange();

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Legacy Safari fallback.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [choice]);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

export function ThemedToaster() {
  const { resolved } = useTheme();
  return (
    <Toaster
      position="bottom-right"
      richColors
      closeButton
      theme={resolved}
    />
  );
}
